import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type UserAccount, UserAccountFlags } from '@repo/types/account';
import { authApi } from '../api/authApi';

export interface AuthState {
    user: UserAccount | null;
    token: string | null;
    isAuthenticated: boolean;
}

const initialState: AuthState = {
    user: null,
    token: null,
    isAuthenticated: false,
};

const handleAuthSuccess = (state: AuthState, user: UserAccount, token: string | null) => {
    state.user = user;
    state.token = token;
    state.isAuthenticated = true;
};

const handleAuthLogout = (state: AuthState) => {
    state.user = null;
    state.token = null;
    state.isAuthenticated = false;
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        updateUserProfile: (state, action: PayloadAction<Partial<UserAccount>>) => {
            if (state.user) {
                state.user = Object.assign({}, state.user, action.payload);
            }
        },
    },

    extraReducers: builder => {
        // When user data is fetched successfully
        builder.addMatcher(authApi.endpoints.getUser.matchFulfilled, (state, action) => {
            const { user, token } = action.payload;
            handleAuthSuccess(state, user, token);
        });

        // When login is successful
        builder.addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
            const { user, token } = action.payload;
            handleAuthSuccess(state, user, token);
        });

        // When logout is successful
        builder.addMatcher(authApi.endpoints.logout.matchFulfilled, handleAuthLogout);

        // When logout fails (still clear auth state)
        builder.addMatcher(authApi.endpoints.logout.matchRejected, handleAuthLogout);
    },
});

export const { updateUserProfile } = authSlice.actions;

export default authSlice.reducer;

export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectAuthToken = (state: { auth: AuthState }) => state.auth.token;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectIsMapEditor = (state: { auth: AuthState }) => {
    const user = state.auth.user;
    return user ? (user.flags & UserAccountFlags.GameMapEditor) !== 0 : false;
};

export const selectAuthState = createSelector([selectCurrentUser, selectIsAuthenticated], (user, isAuthenticated) => ({
    user,
    isAuthenticated,
}));
